import type { Progress, ProgressEvent, SkillProgress } from '@shared/schemas';
import { defaultProgress } from '@/contexts/defaults';

/**
 * In-memory mock progress state · seeded from `defaults.ts`. PATCH handlers
 * mutate this object so optimistic-update flows behave realistically across
 * a single FE session in MOCK_MODE.
 *
 * `__resetFixtureState()` resets between test cases.
 */

let _state: Progress = defaultProgress();

export function progressFixture(): Progress {
  return _state;
}

export function patchProgressBranch(
  branchKey: string,
  idArg: string | undefined,
  body: Record<string, unknown>,
): unknown {
  switch (branchKey) {
    case 'last': {
      _state = {
        ..._state,
        last: { ..._state.last, ...body, timestamp: new Date().toISOString() } as typeof _state.last,
      };
      return _state.last;
    }
    case 'tickets': {
      if (!idArg) throw new Error('tickets branch requires id');
      const old = _state.tickets[idArg] ?? { status: 'todo' as const, acChecked: {} };
      const merged = { ...old, ...body };
      _state = {
        ..._state,
        tickets: { ..._state.tickets, [idArg]: merged as typeof old },
      };
      return merged;
    }
    case 'sprints': {
      if (!idArg) throw new Error('sprints branch requires id');
      const old = _state.sprints[idArg] ?? { status: 'available' as const, pctDone: 0 };
      const merged = { ...old, ...body };
      _state = {
        ..._state,
        sprints: { ..._state.sprints, [idArg]: merged as typeof old },
      };
      return merged;
    }
    case 'homework': {
      if (!idArg) throw new Error('homework branch requires id');
      const old = _state.homework[idArg] ?? { status: 'locked' as const };
      const merged = { ...old, ...body };
      _state = {
        ..._state,
        homework: { ..._state.homework, [idArg]: merged as typeof old },
      };
      return merged;
    }
    case 'onboarding': {
      _state = { ..._state, onboarding: { ..._state.onboarding, ...body } };
      return _state.onboarding;
    }
    case 'pomodoro': {
      const base = _state.pomodoro ?? { mode: 'idle' as const, durationSec: 0 };
      const merged = { ...base, ...body };
      _state = { ..._state, pomodoro: merged as typeof base };
      return _state.pomodoro;
    }
    case 'cockpit': {
      if (idArg) {
        const old = _state.cockpit.flashcards[idArg] ?? null;
        const base = old ?? {
          termId: idArg,
          ef: 2.5,
          intervalDays: 0,
          reps: 0,
          lastReviewAt: new Date(0).toISOString(),
          nextDueAt: new Date().toISOString(),
        };
        const merged = { ...base, ...body, termId: idArg };
        _state = {
          ..._state,
          cockpit: {
            ..._state.cockpit,
            flashcards: { ..._state.cockpit.flashcards, [idArg]: merged as typeof base },
          },
        };
        return merged;
      }
      _state = { ..._state, cockpit: { ..._state.cockpit, ...body } };
      return _state.cockpit;
    }
    case 'achievements': {
      _state = { ..._state, achievements: { ..._state.achievements, ...body } as typeof _state.achievements };
      return _state.achievements;
    }
    case 'capstone': {
      _state = { ..._state, capstone: { ..._state.capstone, 'block-1': { ...(_state.capstone['block-1'] ?? {}), ...body } as NonNullable<typeof _state.capstone['block-1']> } };
      return _state.capstone;
    }
    case 'events': {
      // Mirror the server patcher (server/routes/progress.ts:444): append the
      // batch, dedupe by id (idempotent retry), prune к 90-day window.
      const incoming = Array.isArray((body as { events?: unknown }).events)
        ? ((body as { events: ProgressEvent[] }).events)
        : [];
      const byId = new Map<string, ProgressEvent>();
      for (const e of [...(_state.events ?? []), ...incoming]) byId.set(e.id, e);
      const cutoff = Date.now() - 90 * 24 * 60 * 60 * 1000;
      const merged = [...byId.values()].filter((e) => {
        const t = Date.parse(e.at);
        return Number.isFinite(t) && t >= cutoff;
      });
      _state = { ..._state, events: merged };
      return _state.events;
    }
    default:
      throw new Error(`Unknown progress branch: ${branchKey}`);
  }
}

export function skillsFixture(): Record<string, SkillProgress> {
  return _state.skills;
}

/**
 * Matches `supabase/functions/progress/index.ts` GET /ctfl-readiness shape:
 *   `{chapters: Record<chapterId, fraction0to1>}` (CTFL-Ch1..6).
 * Fixed FS.2 from a stale array-shape (legacy Hono-era parity drift).
 */
export function ctflReadinessFixture(): Record<string, number> {
  return {
    'CTFL-Ch1': 0,
    'CTFL-Ch2': 0,
    'CTFL-Ch3': 0,
    'CTFL-Ch4': 0,
    'CTFL-Ch5': 0,
    'CTFL-Ch6': 0,
  };
}

export function __resetFixtureState(): void {
  _state = defaultProgress();
}
